import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  IconButton,
  Alert,
  Stack,
  Paper,
  Modal,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import axios from 'axios';

const API_ITEM_DETAIL = '/suggestedOrders/itemDetail';
const IMAGE_BASE_URL_SMALL = 'https://media.server.theshootingwarehouse.com/small/';
const IMAGE_BASE_URL_HIRES = 'https://media.server.theshootingwarehouse.com/hires/';

const RowDetailModal = ({ open, handleClose, itemNumber }) => {
  const theme = useTheme();
  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [hasImage, setHasImage] = useState(true);

  const handleInternalClose = useCallback(() => {
    setIsImageModalOpen(false);
    handleClose();

    setTimeout(() => {
      setProduct(null);
      setError(null);
      setIsLoading(false);
      setHasImage(true);
    }, 300);
  }, [handleClose]);

  useEffect(() => {
    if (open && itemNumber) {
      const fetchDetails = async () => {
        setIsLoading(true);
        setError(null);
        setProduct(null);
        setHasImage(true);
        setIsImageModalOpen(false);
        try {
          const response = await axios.post(API_ITEM_DETAIL, { itemNumber });

          if (response.data && response.data.length > 0) {
            setProduct(response.data[0]);
          } else {
            setError(`No details found for item ${itemNumber}.`);
          }
        } catch (error) {
          console.error('Failed to fetch product details:', error);
          setError(error.response?.data?.message || 'Failed to load product details.');
        } finally {
          setIsLoading(false);
        }
      };
      fetchDetails();
    }
  }, [itemNumber, open]);

  const handleImageClick = useCallback(() => setIsImageModalOpen(true), []);
  const handleImageModalClose = useCallback(() => setIsImageModalOpen(false), []);

  const formatSpecificationValue = (value) => {
    if (Array.isArray(value)) {
      return value.length ? value.join(', ') : 'N/A';
    }

    if (value === undefined || value === null || value === '') {
      return 'N/A';
    }

    return value;
  };

  const specificationEntries = useMemo(() => {
    if (!product?.facets) return [];

    return Object.entries(product.facets)
      .filter(([, value]) => {
        if (Array.isArray(value)) {
          return value.length > 0;
        }
        return value !== undefined && value !== null && value !== '';
      })
      .map(([key, value]) => ({
        key,
        label: key
          .split('_')
          .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
          .join(' '),
        value: formatSpecificationValue(value),
      }));
  }, [product?.facets]);

  const metaDetails = useMemo(
    () => [
      { label: 'Item #', value: product?.itemno },
      { label: 'UPC #', value: product?.upc },
      { label: 'MFR #', value: product?.mfgino },
      { label: 'Department', value: product?.department },
      { label: 'Category', value: product?.category },
    ],
    [product]
  );

  const titleText = useMemo(() => {
    if (!product) return '';
    const headline = [product.brand_name, product.model]
      .filter(Boolean)
      .join(' — ');
    if (headline) return headline;
    return product.description || product.item_description || `Item ${product.itemno ?? ''}`;
  }, [product]);

  const subtitleText = useMemo(() => {
    if (!product) return '';
    const parts = [product.series, product.category_group, product.department]
      .filter(Boolean)
      .map((part) => part.trim());
    return parts.join(' — ');
  }, [product]);

  const renderMetaDetail = ({ label, value }, index) => (
    <Box
      key={label}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        gap: 0.75,
        minWidth: { xs: 'calc(50% - 12px)', sm: 140 },
        flex: { xs: '1 1 calc(50% - 12px)', md: '1 1 auto' },
        flexShrink: 1,
        backgroundColor:
          index % 2 === 0 ? theme.palette.action.hover : theme.palette.background.paper,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        px: { xs: 1.25, md: 1.75 },
        py: 1,
      }}
    >
      <Typography
        variant='body2'
        sx={{ fontWeight: 700, whiteSpace: 'pre-wrap', fontSize: { xs: '0.85rem', md: '0.95rem' } }}
      >
        {`${label}:`}
      </Typography>
      <Typography
        variant='body2'
        color='text.primary'
        sx={{ fontSize: { xs: '0.95rem', md: '1.05rem' }, fontWeight: 600 }}
      >
        {formatSpecificationValue(value)}
      </Typography>
    </Box>
  );

  const renderFacetRow = ({ label, value }, index) => (
    <Box
      key={`${label}-${index}`}
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 2,
        alignItems: 'center',
        backgroundColor:
          index % 2 === 0 ? theme.palette.action.hover : theme.palette.background.paper,
        px: { xs: 1.5, md: 2 },
        py: 1,
      }}
    >
      <Typography
        variant='body2'
        sx={{ fontWeight: 600, fontSize: { xs: '0.9rem', md: '1rem' } }}
      >
        {label}
      </Typography>
      <Typography
        variant='body2'
        sx={{ fontSize: { xs: '0.9rem', md: '1rem' }, textAlign: 'right' }}
      >
        {value}
      </Typography>
    </Box>
  );

  return (
    <>
      <Modal open={open} onClose={handleInternalClose} keepMounted>
        <Paper
          elevation={10}
          sx={{
            width: 'min(95vw, 1400px)',
            maxHeight: '90vh',
            overflowY: 'auto',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            borderRadius: 3,
          }}
        >
          {isLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 420,
                py: 6,
              }}
            >
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ p: { xs: 3, md: 4 } }}>
              <Alert severity='error' sx={{ width: '100%' }}>
                {error}
              </Alert>
            </Box>
          ) : product ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: { md: '620px' },
                pt: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  pr: { xs: 1.5, md: 3 },
                  pb: 1,
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  background:
                    theme.palette.mode === 'light'
                      ? theme.palette.background.paper
                      : theme.palette.background.default,
                }}
              >
                <IconButton
                  aria-label='close modal'
                  onClick={handleInternalClose}
                  size='small'
                  sx={{
                    bgcolor: theme.palette.mode === 'light'
                      ? 'rgba(25, 118, 210, 0.12)'
                      : 'rgba(144, 202, 249, 0.18)',
                    color: theme.palette.primary.main,
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: 2,
                    '&:hover': {
                      bgcolor: theme.palette.mode === 'light'
                        ? 'rgba(25, 118, 210, 0.22)'
                        : 'rgba(144, 202, 249, 0.28)',
                    },
                  }}
                >
                  <CloseIcon fontSize='small' />
                </IconButton>
              </Box>

              <Box
                sx={{
                  px: { xs: 3, md: 5 },
                  pb: { xs: 4, md: 6 },
                  display: 'flex',
                  flexDirection: 'column',
                  gap: { xs: 2.5, md: 3.5 },
                }}
              >
                <Box sx={{ width: '100%' }}>
                  <Typography
                    id='product-detail-dialog-title'
                    variant='h4'
                    sx={{
                      color: theme.palette.primary.main,
                      fontWeight: 700,
                      lineHeight: 1.2,
                      width: { md: 'calc(100% - 56px)' },
                      textAlign: 'justify',
                      fontSize: { xs: '1.6rem', md: '2rem' },
                    }}
                  >
                    {titleText}
                  </Typography>
                  {subtitleText && (
                    <Typography
                      variant='subtitle1'
                      color='text.secondary'
                      sx={{
                        width: { md: 'calc(100% - 56px)' },
                        textAlign: 'justify',
                        lineHeight: 1.1,
                        mt: 1,
                        fontStyle: 'italic',
                      }}
                    >
                      {subtitleText}
                    </Typography>
                  )}
                </Box>

                <Box
                  sx={{
                    width: '100%',
                    height: '3px',
                    backgroundColor: theme.palette.primary.main,
                  }}
                />

                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    alignItems: 'stretch',
                    columnGap: { xs: 2, md: 1.5 },
                    rowGap: 2,
                  }}
                >
                  {metaDetails.map((detail, index) => renderMetaDetail(detail, index))}
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', lg: 'row' },
                    alignItems: 'stretch',
                    width: '100%',
                    gap: { xs: 3, lg: 4 },
                    minHeight: { xs: 'auto', lg: '420px' },
                  }}
                >
                  <Box
                    sx={{
                      pt: 2,
                      px: { xs: 1, md: 2 },
                      width: { xs: '100%', lg: '55%' },
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 3,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        display: { xs: 'flex', lg: 'none' },
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: '45vh',
                        py: 2,
                      }}
                    >
                      {hasImage ? (
                        <Box
                          component='img'
                          src={`${IMAGE_BASE_URL_SMALL}${product.itemno}.jpg`}
                          alt={`${product.model || 'Product'} thumbnail`}
                          sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            setHasImage(false);
                          }}
                        />
                      ) : (
                        <Stack spacing={1} alignItems='center' textAlign='center'>
                          <Typography variant='subtitle2' color='text.secondary'>
                            Image not available
                          </Typography>
                          <Typography variant='body2' color='text.secondary'>
                            High-resolution product imagery is currently unavailable.
                          </Typography>
                        </Stack>
                      )}
                    </Box>

                    <Box
                      sx={{
                        flexGrow: 1,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        overflow: 'auto',
                      }}
                    >
                      {specificationEntries.length > 0 ? (
                        specificationEntries.map((entry, index) => renderFacetRow(entry, index))
                      ) : (
                        <Box sx={{ p: 2 }}>
                          <Typography variant='body2' color='text.secondary'>
                            No additional specifications available.
                          </Typography>
                        </Box>
                      )}
                    </Box>

                  </Box>

                  <Box
                    sx={{
                      width: { xs: '100%', lg: '45%' },
                      display: 'flex',
                      flexDirection: 'column',
                      px: { xs: 1, md: 2 },
                    }}
                  >
                    <Box
                      sx={{
                        display: { xs: 'none', lg: 'flex' },
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        maxHeight: '45vh',
                        py: 2,
                        position: 'relative',
                      }}
                    >
                      {hasImage && (
                        <IconButton
                          aria-label='Zoom image'
                          onClick={handleImageClick}
                          sx={{
                            position: 'absolute',
                            top: 16,
                            right: 16,
                            zIndex: 1,
                            backgroundColor: 'rgba(33, 33, 33, 0.1)',
                            '&:hover': { backgroundColor: 'rgba(33, 33, 33, 0.2)' },
                          }}
                        >
                          <ZoomInIcon />
                        </IconButton>
                      )}
                      {hasImage ? (
                        <Box
                          component='img'
                          src={`${IMAGE_BASE_URL_SMALL}${product.itemno}.jpg`}
                          alt={`${product.model || 'Product'} thumbnail`}
                          sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            setHasImage(false);
                          }}
                        />
                      ) : (
                        <Stack spacing={1} alignItems='center' textAlign='center'>
                          <Typography variant='subtitle2' color='text.secondary'>
                            Image not available
                          </Typography>
                          <Typography variant='body2' color='text.secondary'>
                            High-resolution product imagery is currently unavailable.
                          </Typography>
                        </Stack>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                p: { xs: 3, md: 4 },
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Typography color='text.secondary'>
                Product details could not be loaded.
              </Typography>
            </Box>
          )}
        </Paper>
      </Modal>

      {product && (
        <Dialog
          open={isImageModalOpen}
          onClose={handleImageModalClose}
          maxWidth='xl'
          aria-labelledby='image-detail-dialog-title'
        >
          <DialogTitle
            id='image-detail-dialog-title'
            sx={{
              py: 1.5,
              px: 2.5,
              borderBottom: `1px solid ${theme.palette.divider}`,
              backgroundColor:
                theme.palette.mode === 'light'
                  ? theme.palette.grey[50]
                  : theme.palette.background.default,
            }}
          >
            <Stack direction='row' justifyContent='space-between' alignItems='center'>
              <Typography variant='h6' component='div' sx={{ fontSize: '1.05rem' }}>
                {product.brand_name} - {product.model} (Item: {product.itemno})
              </Typography>
              <IconButton
                aria-label='close image view'
                onClick={handleImageModalClose}
                sx={{
                  color: theme.palette.text.secondary,
                  backgroundColor: theme.palette.action.hover,
                  '&:hover': { backgroundColor: theme.palette.action.selected },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent
            sx={{ p: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          >
            <img
              src={`${IMAGE_BASE_URL_HIRES}${product.itemno}.png`}
              alt={`${product.model || 'Product'} high-res`}
              style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
              onError={(e) => {
                e.target.alt = 'High-res image not available';
              }}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default RowDetailModal;
